import { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { useParallax } from '@/hooks/useParallax';

const NightWindParticles = lazy(() => import('@/components/three/NightWindParticles'));

interface RoomPageProps {
  roomName: string;
  roomTitle: string;
  description: string;
  works?: {
    title: string;
    description: string;
  }[];
  heroGradient?: {
    from: string;
    via?: string;
    to: string;
  };
}

const RoomPage = ({ roomName, roomTitle, description, works = [], heroGradient }: RoomPageProps) => {
  const { scrollY, isMobile } = useParallax();
  
  // デフォルトのグラデーション
  const gradient = heroGradient || { from: '#031D1A', to: '#0A3A36' };
  const gradientStyle = gradient.via 
    ? `linear-gradient(to bottom right, ${gradient.from}, ${gradient.via}, ${gradient.to})`
    : `linear-gradient(to bottom right, ${gradient.from}, ${gradient.to})`;

  return (
    <div className="min-h-screen relative">
      {/* 光の帯 */}
      <div className="light-band" />
      {/* ノイズテクスチャ */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        {/* 背景グラデーション */}
        <div className="absolute inset-0" style={{ background: gradientStyle }} />
        
        {/* パーティクル */}
        <div className="absolute inset-0">
          <Suspense fallback={null}>
            <NightWindParticles className="w-full h-full" />
          </Suspense>
        </div>

        {/* 部屋名ラベル */}
        <div className="absolute left-8 md:left-12 top-28 z-10">
          <p className="text-xs font-display tracking-[0.2em] text-muted-foreground/50">
            Studio: {roomName}
          </p>
        </div>

        {/* メインコンテンツ */}
        <div 
          className="relative z-10 w-full px-8 md:px-12 lg:px-24 pb-24 md:pb-32"
          style={{
            transform: `translateY(${isMobile ? 0 : scrollY * 0.05}px)`,
          }}
        >
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-5xl font-display font-light text-foreground tracking-[0.15em] mb-8">
              {roomTitle}
            </h1>
          </div>
        </div>
      </section>

      {/* Explanation */}
      <section className="relative py-24 md:py-32 px-8 md:px-12 lg:px-24">
        <div className="max-w-3xl ml-0 md:ml-12">
          <p className="text-base md:text-lg text-muted-foreground/80 font-light leading-[2] tracking-wide">
            {description}
          </p>
        </div>
      </section>

      {/* Works / Contents */}
      {works.length > 0 && (
        <section className="relative py-24 md:py-32 px-8 md:px-12 lg:px-24">
          <div className="mb-16">
            <p className="text-xs font-display tracking-[0.3em] text-muted-foreground/50 mb-4">
              WORKS
            </p>
          </div>

          <div className="space-y-8 md:space-y-0 md:grid md:grid-cols-12 md:gap-8">
            {works.map((work, index) => {
              const offsets = [
                { x: 0, col: 'md:col-span-7 md:col-start-4' },
                { x: 24, col: 'md:col-span-5 md:col-start-8' },
                { x: -16, col: 'md:col-span-6 md:col-start-2' },
                { x: 12, col: 'md:col-span-5 md:col-start-7' },
              ];
              const offset = offsets[index % 4];
              
              return (
                <div
                  key={index}
                  className={`${offset.col}`}
                  style={{
                    transform: `translateY(${(index % 3) * 12}px)`,
                  }}
                >
                  <div className="glass-card px-8 py-8 group md:max-w-[48ch]">
                    <h3 className="font-display text-base font-light tracking-[0.1em] text-foreground/85 mb-3 group-hover:tracking-[0.12em] transition-all duration-500">
                      {work.title}
                    </h3>
                    <p className="text-sm text-muted-foreground/60 font-light leading-relaxed">
                      {work.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-32 md:py-48 px-8 md:px-12 lg:px-24">
        <div className="max-w-md mx-auto text-center">
          <div className="glass-card px-10 py-12">
            <a
              href="https://x.com/awijirun?s=21&t=fSHJ9CenI6va24cOV6FasQ"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <p className="text-sm text-muted-foreground/70 font-light tracking-wide mb-8">
                そっと相談してみる
              </p>
              <span className="inline-block text-xs font-display tracking-[0.2em] text-foreground/70 hover:text-foreground/90 transition-colors duration-500">
                CONTACT →
              </span>
              <p className="text-xs text-muted-foreground/40 mt-6">
                返信はゆっくりです
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 relative">
        <div className="container text-center">
          <Link 
            to="/"
            className="text-xs font-display tracking-[0.15em] text-muted-foreground/50 hover:text-muted-foreground/70 transition-colors duration-300"
          >
            ← Back to Top
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default RoomPage;